// Fixture set for the "first-narrative-report" scene: a demo officer with two
// finished cases, and the payload that finishes the case the scene creates by
// uploading /academy/samples/narrative-retail-theft.mp3. Everything here is
// fictional. Row shapes follow client/src/db/schema.ts (cases, suggestions,
// case_scores, case_files, report_preferences, users).
import { SAMPLES_BASE } from "../../../scenes/types";
import type { FixtureSet, FixtureCase, FixtureCompletion } from "../types";

const USER_ID = "a0000000-0000-4000-8000-000000000001";

const yearsAgo = (n: number) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - n);
  return d.toISOString();
};

const user = {
  id: USER_ID,
  agency_id: null,
  stripe_customer_id: "cus_academy_demo",
  name: "Officer J. Ramirez",
  email: "demo@policenarratives.ai",
  badgeID: "406",
  department: "Riverton Police Department",
  avatarUrl: null,
  external_auth_provider: "email",
  platform_review_status: {},
  // Every tour id, so no guided tour pops over the scripted scene. The
  // installer unions in whatever tours exist at runtime as well.
  seen_tutorials: ["add-to-report", "case-categories"],
  academy_progress: {},
  created_at: "2024-01-15T17:12:00.000Z",
  updated_at: yearsAgo(0),
  roles: ["officer"] as string[],
};

const preferences = {
  id: "b0000000-0000-4000-8000-000000000001",
  userId: USER_ID,
  agencyId: null,
  badge_number: true,
  name: true,
  top_sheet_information: false,
  topic_headings: false,
  abbreviate_rank: true,
  capitalize_names: false,
  capitalize_rank: false,
  day_of_week: true,
  date_format: "MMMM DD YYYY",
  time_format: "military_time",
  perspective: "first_person",
  subject_reference_style: "role_with_last_name_after_first_mention",
  header: null,
  footer: null,
  additional_prompt: null,
  rank: "Officer",
  include_call_sign: false,
  call_sign: null,
  call_sign_format: "before_name",
  call_sign_mention: "first_mention",
  statute_state: "California",
  common_spellings: [] as string[],
  default_template_id: null,
  created_at: "2024-01-15T17:12:00.000Z",
  updated_at: "2024-01-15T17:12:00.000Z",
};

// ---------------------------------------------------------------------------
// Two finished cases so the list is not empty when the scene opens.

const CASE_1_ID = "c0000000-0000-4000-8000-000000000101";
const CASE_2_ID = "c0000000-0000-4000-8000-000000000102";

const case1Content = [
  "<h1>Vehicle Burglary - 26-004127</h1>",
  "<p>On Tuesday, October 20, 2026, at approximately 0715 hours, I, Officer J. Ramirez #406, was dispatched to 2210 Sycamore Court regarding a vehicle burglary that had occurred overnight.</p>",
  "<p>On arrival I contacted the reporting party, Elena Vasquez, who told me her gray 2019 Honda Civic had been parked in the driveway since approximately 2100 hours the previous evening. At approximately 0640 hours she found the front passenger window shattered and the glove box open.</p>",
  "<p>Vasquez reported a black Garmin GPS unit and approximately $40 in cash missing from the center console. No other property was taken. I photographed the damage and the broken glass on the passenger seat and collected a partial shoe impression from the hood of the vehicle.</p>",
  "<p>I canvassed the neighboring residences. The occupant of 2214 Sycamore Court, Thomas Grier, stated he heard a car alarm at approximately 0300 hours but did not look outside. No residences on the block had exterior cameras facing the driveway.</p>",
  "<p>I provided Vasquez with the case number and the victim information pamphlet. This case is forwarded to the Investigations Bureau for follow-up.</p>",
].join("");

const case2Content = [
  "<h1>Domestic Disturbance - 26-004133</h1>",
  "<p>On Thursday, October 22, 2026, at approximately 2152 hours, I, Officer J. Ramirez #406, was dispatched to 418 Poplar Street, Apartment 3, regarding a verbal domestic disturbance reported by a neighbor.</p>",
  "<p>On arrival I could hear raised voices from inside the apartment. I knocked and announced myself, and the door was opened by Renee Holloway. I separated Holloway from the second party, Darnell Keane, and spoke to each of them individually.</p>",
  "<p>Holloway stated the argument began over unpaid rent and had remained verbal. Keane gave a consistent account and stated nobody had been touched. I observed no injuries on either party and neither requested medical attention. Both parties denied any history of physical violence.</p>",
  "<p>Keane agreed to stay at his brother's residence for the night and left the location at approximately 2220 hours. I provided Holloway with the domestic violence resource card and explained how to request an emergency protective order should circumstances change.</p>",
  "<p>No crime was established. This report is for documentation purposes.</p>",
].join("");

const existingCases: FixtureCase[] = [
  {
    row: {
      id: CASE_1_ID,
      userId: USER_ID,
      caseNumber: "26-004127",
      incidentDate: "2026-10-20",
      incidentTime: "07:15:00",
      status: "active",
      error: null,
      content: { data: case1Content },
      audioUrl: null,
      audioMetadata: null,
      transcriptionUrl: null,
      statutes: [],
      redactions: [],
      reportDetailLevel: "standard",
      includeTimestampsInNarrative: false,
      hasAttachments: false,
      reportTemplateId: null,
      callSign: null,
      investigationId: null,
      policyReferences: [],
      pendingSuggestionsChanges: null,
      createdAt: "2026-10-20T14:42:00.000Z",
      updatedAt: "2026-10-20T15:10:00.000Z",
      discardedAt: null,
      actions: 3,
    },
    score: { score: 88 },
    suggestions: [
      {
        content: "Note whether Vasquez locked the vehicle; forced entry versus an unlocked door changes the charge.",
        priority: 1,
      },
      {
        content: "Record the estimated value of the Garmin GPS unit for the property section.",
        priority: 2,
      },
    ],
    files: [],
    transcription: null,
  },
  {
    row: {
      id: CASE_2_ID,
      userId: USER_ID,
      caseNumber: "26-004133",
      incidentDate: "2026-10-22",
      incidentTime: "21:52:00",
      status: "active",
      error: null,
      content: { data: case2Content },
      audioUrl: null,
      audioMetadata: null,
      transcriptionUrl: null,
      statutes: [],
      redactions: [],
      reportDetailLevel: "standard",
      includeTimestampsInNarrative: false,
      hasAttachments: false,
      reportTemplateId: null,
      callSign: null,
      investigationId: null,
      policyReferences: [],
      pendingSuggestionsChanges: null,
      createdAt: "2026-10-23T05:18:00.000Z",
      updatedAt: "2026-10-23T05:41:00.000Z",
      discardedAt: null,
      actions: 2,
    },
    score: { score: 91 },
    suggestions: [
      {
        content: "State whether children were present in the apartment.",
        priority: 1,
      },
    ],
    files: [],
    transcription: null,
  },
];

// ---------------------------------------------------------------------------
// Completion payload for the case the scene uploads.

// Verbatim text of /academy/samples/narrative-retail-theft.txt (71.2 s).
const DICTATION =
  "On October 14th, 2026, at approximately 1420 hours, I was dispatched to the Valley Mart at 1850 West Olive Avenue regarding a theft that had just occurred. On arrival I contacted the reporting party, loss prevention officer Daniel Reyes, who told me he had watched a male subject conceal two bottles of liquor inside a black backpack and walk past all points of sale without paying. Reyes stopped the subject just outside the east entrance and recovered both bottles, which he valued at a total of eighty-six dollars and ninety-eight cents. I contacted the subject, who identified himself verbally as Marcus Allen Pierce, date of birth March 3rd, 1991. Pierce was cooperative and told me he took the bottles because he, quote, didn't have the money this week, end quote. I confirmed his identity through a records check, which also showed no outstanding warrants. Reyes provided a written statement and told me the store wished to pursue charges. I photographed the recovered merchandise and returned it to the store. I issued Pierce a citation for petty theft and released him at the scene. Store surveillance footage was requested and will be booked into evidence once it is received. There were no injuries and no use of force. End of report.";

const DICTATION_DURATION = 71.2;

const buildTranscription = (text: string, duration: number) => {
  const tokens = text.split(/\s+/).filter(Boolean);
  const slot = duration / tokens.length;
  const words = tokens.map((word, i) => ({
    word,
    start: Number((i * slot).toFixed(3)),
    end: Number(Math.min(duration, (i + 1) * slot - 0.04).toFixed(3)),
  }));
  return { task: "transcribe", language: "english", duration, text, words };
};

const newCaseContent = [
  "<h1>Petty Theft - Valley Mart</h1>",
  "<p>On Wednesday, October 14, 2026, at approximately 1420 hours, I, Officer J. Ramirez #406, was dispatched to the Valley Mart located at 1850 West Olive Avenue regarding a theft that had just occurred. I arrived at approximately {{arrival time}}.</p>",
  "<p>On arrival I contacted the reporting party, loss prevention officer Daniel Reyes. Reyes told me he had watched a male subject conceal two bottles of liquor inside a black backpack and walk past all points of sale without paying. Reyes stopped the subject just outside the east entrance of the store and recovered both bottles, which he valued at a total of $86.98.</p>",
  "<p>I contacted the subject, who verbally identified himself as Marcus Allen Pierce, date of birth 03/03/1991. Pierce was cooperative. He told me he took the bottles because he \"didn't have the money this week.\" I confirmed Pierce's identity through a records check, which also showed no outstanding warrants.</p>",
  "<p>Reyes provided a written statement and told me the store wished to pursue charges. I photographed the recovered merchandise and returned it to the store.</p>",
  "<p>I issued Pierce a citation for petty theft (PC 484(a)) and released him at the scene. Store surveillance footage was requested and will be booked into evidence once it is received. There were no injuries and no use of force.</p>",
].join("");

const completion: FixtureCompletion = {
  content: { data: newCaseContent },
  audioUrl: `${SAMPLES_BASE}/narrative-retail-theft.mp3`,
  score: { score: 84 },
  suggestions: [
    {
      content: "State your arrival time at the Valley Mart; the dictation only gives the dispatch time.",
      priority: 1,
    },
    {
      content: "Which direction did Pierce leave in after he was released? Note it for any follow-up contact.",
      priority: 2,
    },
    {
      content: "Citation number: add the citation number issued to Pierce for the petty theft charge.",
      priority: 3,
    },
  ],
  statutes: [],
  transcription: buildTranscription(DICTATION, DICTATION_DURATION),
  fileUpdate: {
    processing_status: "completed",
    file_size: "1.12 MB",
    metadata: { duration: DICTATION_DURATION },
  },
};

const firstNarrativeReport: FixtureSet = {
  name: "firstNarrativeReport",
  user,
  preferences,
  cases: existingCases,
  completion,
};

export default firstNarrativeReport;
